<template>
  <el-dialog
    v-model="visible"
    title="Создание смены"
    class="create-schedule-dialog"
    :width="dialogWidth"
    align-center
    @close="resetForm"
  >
    <el-form :model="form" ref="formRef" :label-width="formLabelWidth" hide-required-asterisk>
      <el-form-item label="*Сотрудник" prop="user_id" :rules="[{ required: true, message: 'Выберите сотрудника', trigger: 'change' }]">
        <el-select v-model="form.user_id" placeholder="Выберите">
          <el-option
            v-for="user in employees"
            :key="user.id"
            :label="user.name"
            :value="user.id"
          />
        </el-select>
        <el-alert
          v-if="userError"
          type="error"
          show-icon
          :closable="false"
          class="employee-error-alert"
        >
          <template #title>
            {{ userError }}
            <el-link
              type="primary"
              style="margin-left: 6px;"
              @click="$router.push('/employees')"
            >
              Перейти к настройке доступа
            </el-link>
          </template>
        </el-alert>
      </el-form-item>

      <el-form-item label="*Локация" prop="location_id" :rules="[{ required: true, message: 'Выберите локацию', trigger: 'change' }]">
        <el-select v-model="form.location_id" placeholder="Выберите" :disabled="!form.user_id || !!userError">
          <el-option
            v-for="location in filteredLocations"
            :key="location.id"
            :label="location.name"
            :value="location.id"
            :disabled="isLocationUsed(location)"
          />
        </el-select>
      </el-form-item>

      <el-form-item label="Шаблон смены">
        <div class="template-actions">
          <el-button
            class="template-apply-btn"
            @click="applyTemplateDialogVisible = true"
            :disabled="!form.location_id"
          >
            Применить шаблон
          </el-button>
        </div>
      </el-form-item>
    </el-form>
    <shift-calendar
      v-model:shifts="generatedShifts"
      mode="edit"
      :workingHours="workingHours"
      :disabled="!form.location_id"
    />

    <template #footer>
      <el-button class="dialog-footer-btn dialog-footer-btn-cancel" @click="visible = false">Отмена</el-button>
      <el-button class="dialog-footer-btn dialog-footer-btn-submit" type="primary" @click="submit">Создать</el-button>
    </template>
  </el-dialog>



  <el-dialog
    v-model="applyTemplateDialogVisible"
    title="Применить шаблон"
    width="320"
    class="apply-template-dialog"
  >
    <el-select v-model="selectedTemplate" placeholder="Выберите шаблон" style="width: 100%; margin-bottom: 12px;">
      <el-option
        v-for="template in templates"
        :key="template.id"
        :label="template.name"
        :value="template.id"
      />
    </el-select>
    <el-date-picker
      v-model="dateRange"
      type="daterange"
      start-placeholder="Дата начала"
      end-placeholder="Дата окончания"
      :disabled-date="disabledPastDates"
      range-separator="—"
      unlink-panels
      style="width: 100%; margin-bottom: 12px;"
    />
    <el-button type="primary" @click="applyTemplate">Применить</el-button>
  </el-dialog>
</template>
<script>
import { mapActions, mapState } from 'vuex'
import ShiftCalendar from './ShiftCalendar.vue'
import { generateShiftsFromTemplate } from '@/components/util/scheduleTemplate'

export default {
  name: 'CreateScheduleDialog',
  components: {
    ShiftCalendar
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      form: {
        user_id: '',
        location_id: '',
        date: '',
        start_time: '',
        end_time: ''
      },
      userError: null,
      workingHours: null,
      generatedShifts: [],
      applyTemplateDialogVisible: false,
      selectedTemplate: null,
      dateRange: []
    }
  },
  computed: {
    ...mapState('users', ['employees']),
    ...mapState('cities', {
      locations: state => {
        const flattened = []
        state.cities.forEach(city => {
          city.children?.forEach(branch => {
            // Add branch entry with room_id: null
            flattened.push({
              id: `branch_${branch.id}`,
              name: `${city.name} / ${branch.name}`,
              city_id: city.id,
              branch_id: branch.id,
              room_id: null,
              working_times: (branch.working_times || branch.working || null)
            })
            branch.children?.forEach(room => {
              flattened.push({
                id: `room_${room.id}`,
                name: `${city.name} / ${branch.name} / ${room.name}`,
                city_id: city.id,
                branch_id: branch.id,
                room_id: room.id,
                working_times: (room.working_times || room.working || null)
              })
            })
          })
        })
        return flattened
      }
    }),
    ...mapState('schedules', {
      templates: state => state.schedulesTemplates,
      schedulesList: state => state.schedules
    }),
    dialogWidth() {
      if (typeof window !== 'undefined' && window.innerWidth <= 480) return 'calc(100vw - 20px)'
      if (typeof window !== 'undefined' && window.innerWidth <= 980) return 'min(760px, calc(100vw - 28px))'
      return '760px'
    },
    formLabelWidth() {
      if (typeof window !== 'undefined' && window.innerWidth <= 600) return '84px'
      return '170px'
    },
    visible: {
      get() {
        return this.modelValue
      },
      set(val) {
        this.$emit('update:modelValue', val)
      }
    },
    filteredLocations() {
      if (!this.form.user_id) {
        return [];
      }
      const user = this.employees.find(u => u.id === this.form.user_id);
      if (!user || !user.locations || !user.locations.length) {
        return [];
      }
      // Collect allowed locations based on user's allowed locations
      const allowedLocations = [];
      user.locations.forEach(userLoc => {
        if (userLoc.room_id) {
          // Allow only exact room matches
          this.locations.forEach(loc => {
            if (
              loc.city_id === userLoc.city_id &&
              loc.branch_id === userLoc.branch_id &&
              loc.room_id === userLoc.room_id
            ) {
              allowedLocations.push(loc);
            }
          });
        } else if (userLoc.branch_id && userLoc.room_id === null) {
          // Allow only branch entries (room_id null, branch_id match, city_id match)
          this.locations.forEach(loc => {
            if (
              loc.city_id === userLoc.city_id &&
              loc.branch_id === userLoc.branch_id &&
              loc.room_id === null
            ) {
              allowedLocations.push(loc);
            }
          });
        }
      });
      // Remove duplicates (in case user has multiple locations that include same location)
      const uniqueLocations = [];
      const ids = new Set();
      allowedLocations.forEach(loc => {
        if (!ids.has(loc.id)) {
          ids.add(loc.id);
          uniqueLocations.push(loc);
        }
      });

      const userId = this.form.user_id;
      const schedules = this.schedulesList || [];
      const userSchedule = schedules.find(s => String(s.id) === String(userId));
      if (!userSchedule) {
        return uniqueLocations;
      }

      const userKey = `${userSchedule.city_id}_${userSchedule.branch_id}_${userSchedule.room_id ?? 0}`;

      return uniqueLocations.filter(loc => {
        const locKey = `${loc.city_id}_${loc.branch_id}_${loc.room_id ?? 0}`;
        const isUsed = locKey === userKey;
        return !isUsed;
      });
    }
  },
  methods: {
    ...mapActions('schedules', ['saveSchedule']),
    resetForm() {
      this.form = {
        user_id: '',
        location_id: '',
        date: '',
        start_time: '',
        end_time: ''
      }
      this.userError = ''
      this.workingHours = null
      this.generatedShifts = []
    },
    checkUserAccess(userId, locationId) {
      const user = this.employees.find(u => u.id === userId)
      if (!user) {
        this.$message.error('Сотрудник не найден.');
        return false;
      }
      if (!user.locations || !user.locations.length) {
        this.$message.error('Сотруднику не назначена локация. Настройте доступ перед созданием смены.');
        return false;
      }
      const selectedLocation = this.locations.find(loc => loc.id === locationId)
      if (!selectedLocation) {
        return false
      }
      for (const loc of user.locations) {
        if (loc.room_id) {
          if (
            loc.room_id === selectedLocation.room_id &&
            loc.branch_id === selectedLocation.branch_id &&
            loc.city_id === selectedLocation.city_id
          ) {
            return true
          }
        } else if (loc.branch_id) {
          if (
            loc.branch_id === selectedLocation.branch_id &&
            loc.city_id === selectedLocation.city_id
          ) {
            return true
          }
        } else if (loc.city_id) {
          this.$message.error('Сотруднику необходимо назначить офис/кабинет')
          return false
        }
      }
      return false
    },
    disabledPastDates(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date < today;
    },
    async submit() {
      if (!this.checkUserAccess(this.form.user_id, this.form.location_id)) return
      await this.$refs.formRef.validate()

      const location = this.locations.find(loc => loc.id === this.form.location_id)
      if (!location) {
        this.$message.error('Локация не найдена')
        return
      }
      if (!this.generatedShifts.length) {
        this.$message.warning('Нет смен для сохранения — примените шаблон или выделите дни в календаре')
        return
      }

      const payload = {
        user_id: this.form.user_id,
        city_id: location.city_id,
        branch_id: location.branch_id,
        room_id: location.room_id,
        shifts: this.generatedShifts.map(shift => ({
          date: shift.date,
          start_time: shift.start_time,
          end_time: shift.end_time,
          breaks: Array.isArray(shift.breaks)
            ? shift.breaks.map(b => ({
              type: b.type,
              start: b.start_time,
              end: b.end_time,
              date: shift.date
            }))
            : []
        }))
      }
      try {
        await this.saveSchedule(payload)   // bulk сохранение через стор
        this.$message.success('Смены сохранены')
        this.visible = false
        this.resetForm()
      } catch (e) {
        this.$message.error('Ошибка при сохранении смен')
      }
    },
    applyTemplate() {
      const template = this.templates.find(t => t.id === this.selectedTemplate)
      if (!template || !this.dateRange.length || !this.workingHours) {
        this.$message.error('Выберите шаблон, даты и локацию')
        return
      }
      const [startDate, endDate] = this.dateRange
      this.generatedShifts = []
      const shifts = generateShiftsFromTemplate(
        template,
        startDate,
        endDate,
        this.workingHours
      )
      this.generatedShifts = [...shifts]
      this.$message.success('Смены сгенерированы')
      this.selectedTemplate = null
      this.dateRange = []
      this.applyTemplateDialogVisible = false
    },
    isLocationUsed(location) {
      const userId = this.form.user_id;
      const existingShifts = this.$store.state.schedules.list || [];
      return existingShifts.some(
        s =>
          String(s.user_id) === String(userId) &&
          s.city_id === location.city_id &&
          s.branch_id === location.branch_id &&
          s.room_id === location.room_id
      );
    },
  },
  watch: {
    'form.user_id'(newVal) {
      if (!newVal) {
        this.userError = '';
        return;
      }
      const user = this.employees.find(u => u.id === newVal);
      if (!user || !user.locations || !user.locations.length) {
        this.userError = 'Сотруднику не назначена локация. Настройте доступ перед созданием смены.';
        return;
      }
      // Check if all locations are only city-level (no branch_id and no room_id)
      const onlyCityLevel = user.locations.every(loc => !loc.branch_id && !loc.room_id && !!loc.city_id);
      if (onlyCityLevel) {
        this.userError = 'Сотруднику необходимо назначить офис/кабинет';
        return;
      }
      this.userError = '';
    },
    'form.location_id'(newVal) {
      if (!newVal) {
        this.workingHours = null;
        return;
      }
      const location = this.locations.find(loc => loc.id === newVal);
      if (location && location.working_times) {
        this.workingHours = location.working_times;
      } else {
        this.workingHours = null;
      }
    }
  },
}
</script>

<style>
.create-schedule-dialog {
  background-color: #10244d !important;
  background:
    linear-gradient(rgb(0 108 254 / 22%), rgb(0 108 254 / 22%)),
    linear-gradient(90deg, #030b1b 0%, #15264e 100%) !important;
  background-image:
    linear-gradient(rgb(0 108 254 / 22%), rgb(0 108 254 / 22%)),
    linear-gradient(90deg, #030b1b 0%, #15264e 100%) !important;
  border: 1px solid rgba(44, 86, 163, 0.9);
  border-radius: 0;
  box-shadow: 0 28px 80px rgba(0, 6, 19, 0.48);
  font-family: "Bahnschrift", "DIN Alternate", "Oswald", sans-serif;
  width: min(760px, calc(100vw - 40px)) !important;
  max-width: min(760px, calc(100vw - 40px)) !important;
}

.apply-template-dialog {
  background: var(--appointment-panel);
  border: 1px solid var(--cal-border);
  box-shadow: var(--cal-shadow-card);
  font-family: "Bahnschrift", "DIN Alternate", "Oswald", sans-serif;
}

.create-schedule-dialog .el-dialog__header,
.create-schedule-dialog .el-dialog__body,
.create-schedule-dialog .el-dialog__footer,
.apply-template-dialog .el-dialog__header,
.apply-template-dialog .el-dialog__body,
.apply-template-dialog .el-dialog__footer {
  background: transparent;
}

.create-schedule-dialog .el-dialog__title,
.apply-template-dialog .el-dialog__title {
  color: var(--cal-text-inverse);
  display: block;
  text-align: center;
}

.create-schedule-dialog .el-dialog__header {
  padding: 16px 24px 4px;
}

.create-schedule-dialog .el-dialog__title {
  font-family: "Bahnschrift", "DIN Alternate", "Oswald", sans-serif;
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
}

.create-schedule-dialog .el-dialog__body {
  padding: 16px 24px 0;
  flex: 1 1 auto !important;
  overflow-y: auto !important;
  max-height: none !important;
}

.create-schedule-dialog .el-dialog__footer {
  padding: 12px 24px 16px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 18px;
}

.create-schedule-dialog .el-dialog__headerbtn,
.apply-template-dialog .el-dialog__headerbtn {
  top: 22px;
  right: 24px;
  color: #031230;
  font-size: 26px;
  font-weight: 700;
}

.create-schedule-dialog .el-form-item__label,
.apply-template-dialog .el-form-item__label {
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  white-space: nowrap;
  font-family: "Bahnschrift", "DIN Alternate", "Oswald", sans-serif;
  font-size: 18px;
  font-weight: 300;
  line-height: 1;
  min-height: 44px;
  padding-right: 8px;
  text-align: left;
}

.create-schedule-dialog .el-input__wrapper,
.create-schedule-dialog .el-select__wrapper,
.create-schedule-dialog .el-date-editor.el-input__wrapper,
.apply-template-dialog .el-input__wrapper,
.apply-template-dialog .el-select__wrapper,
.apply-template-dialog .el-date-editor.el-input__wrapper {
  background: #07142d !important;
  border-radius: 8px !important;
  box-shadow: 0 0 0 1px rgba(48, 87, 154, 0.85) inset !important;
  color: #00c3fe !important;
  border: none !important;
  min-height: 44px !important;
}

.create-schedule-dialog .el-input__wrapper:hover,
.create-schedule-dialog .el-select__wrapper:hover,
.create-schedule-dialog .el-input__wrapper.is-focus,
.create-schedule-dialog .el-select__wrapper.is-focused,
.apply-template-dialog .el-input__wrapper:hover,
.apply-template-dialog .el-select__wrapper:hover {
  background: #0d2757 !important;
  box-shadow: 0 0 0 1px #3f6fb8 inset !important;
}

.create-schedule-dialog .el-input__inner,
.create-schedule-dialog .el-select__placeholder,
.create-schedule-dialog .el-select__selected-item,
.create-schedule-dialog .el-range-input,
.apply-template-dialog .el-input__inner,
.apply-template-dialog .el-select__placeholder,
.apply-template-dialog .el-select__selected-item,
.apply-template-dialog .el-range-input {
  color: #00c3fe !important;
  -webkit-text-fill-color: #00c3fe !important;
  font-family: "Bahnschrift", "DIN Alternate", "Oswald", sans-serif !important;
  font-size: 16px;
  font-weight: 300;
}

.create-schedule-dialog .el-select__placeholder.is-transparent,
.apply-template-dialog .el-select__placeholder.is-transparent {
  color: #00c3fe !important;
  -webkit-text-fill-color: #00c3fe !important;
}

.create-schedule-dialog .el-select__caret,
.apply-template-dialog .el-select__caret,
.create-schedule-dialog .el-input__icon,
.apply-template-dialog .el-input__icon {
  color: #00c3fe !important;
}

.create-schedule-dialog .el-button--default,
.apply-template-dialog .el-button--default {
  background: #0c224b !important;
  border: none !important;
  box-shadow: 0 0 0 1px rgba(118, 159, 232, 0.25) inset !important;
  color: #ffffff !important;
  min-height: 54px;
  padding-inline: 28px;
}

.create-schedule-dialog .el-button--primary,
.apply-template-dialog .el-button--primary {
  background: #00c3fe !important;
  border-color: transparent !important;
  color: #ffffff !important;
  min-height: 44px;
  padding-inline: 28px;
}

.create-schedule-dialog .el-button--primary.is-plain {
  background: #0c224b !important;
  border: none !important;
  box-shadow: 0 0 0 1px #1b3f7a inset !important;
  color: #00c3fe !important;
}

.create-schedule-dialog .template-actions {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  min-width: 0;
}

.create-schedule-dialog .el-form-item__content {
  min-width: 0 !important;
}

.create-schedule-dialog .el-form {
  margin-top: 6px;
}

.create-schedule-dialog .shift-calendar,
.create-schedule-dialog .shift-calendar--edit {
  margin-top: 18px;
}

.create-schedule-dialog .template-apply-btn {
  min-height: 34px !important;
  padding: 0 18px !important;
  border-radius: 8px !important;
  background:
    linear-gradient(rgba(4, 35, 83, 0.97), rgba(4, 35, 83, 0.97)),
    linear-gradient(299deg, #030b1b 13.22%, #15264e 86.78%) !important;
  border: none !important;
  box-shadow: 0 0 0 1px rgba(93, 126, 198, 0.18) inset !important;
  color: #00c3fe !important;
  font-size: 15px !important;
  line-height: 1;
  font-weight: 300;
  font-family: "Bahnschrift", "DIN Alternate", "Oswald", sans-serif !important;
}

.create-schedule-dialog .template-apply-btn:not(.is-disabled):hover,
.create-schedule-dialog .template-apply-btn:not(.is-disabled):focus {
  background: #0d2757 !important;
  box-shadow: 0 0 0 1px #3f6fb8 inset !important;
  color: #00c3fe !important;
}

.create-schedule-dialog .template-apply-btn.is-disabled,
.create-schedule-dialog .template-apply-btn.is-disabled:hover,
.create-schedule-dialog .template-apply-btn.is-disabled:focus {
  background:
    linear-gradient(rgba(4, 35, 83, 0.97), rgba(4, 35, 83, 0.97)),
    linear-gradient(299deg, #030b1b 13.22%, #15264e 86.78%) !important;
  box-shadow: none !important;
  color: rgba(0, 195, 254, 0.72) !important;
  opacity: 1;
}

.create-schedule-dialog .template-apply-btn.is-disabled span,
.create-schedule-dialog .template-apply-btn.is-disabled:hover span,
.create-schedule-dialog .template-apply-btn.is-disabled:focus span {
  color: rgba(0, 195, 254, 0.72) !important;
}

.create-schedule-dialog .el-button--primary.is-disabled,
.create-schedule-dialog .el-button--primary.is-disabled:hover,
.create-schedule-dialog .el-button--primary.is-disabled:focus {
  background: var(--appointment-primary);
  color: var(--appointment-primary-text);
  opacity: 0.55;
}

.create-schedule-dialog .dialog-footer-btn {
  min-width: 132px;
  height: 44px;
  min-height: 44px;
  padding: 0 24px !important;
  border-radius: 12px;
  font-size: 16px;
  line-height: 1;
  font-weight: 700;
  font-family: "Bahnschrift", "DIN Alternate", "Oswald", sans-serif !important;
}

.create-schedule-dialog .dialog-footer-btn-cancel {
  background: #0c224b !important;
  border: none !important;
  color: #ffffff !important;
  box-shadow: 0 0 0 1px rgba(118, 159, 232, 0.25) inset !important;
}

.create-schedule-dialog .dialog-footer-btn-cancel:hover,
.create-schedule-dialog .dialog-footer-btn-cancel:focus {
  background: #0d2757 !important;
  color: #ffffff !important;
}

.create-schedule-dialog .dialog-footer-btn-submit {
  min-width: 160px;
  background: #00c3fe !important;
  border: none !important;
  color: #ffffff !important;
}

.create-schedule-dialog .dialog-footer-btn-submit:hover,
.create-schedule-dialog .dialog-footer-btn-submit:focus {
  background: #00b0e5 !important;
}

.create-schedule-dialog .el-alert {
  background: rgba(14, 28, 61, 0.92);
  border-color: var(--cal-border);
}

.create-schedule-dialog .el-alert__title,
.create-schedule-dialog .el-link {
  color: var(--cal-text-inverse);
}

@media (max-width: 860px) {
  .create-schedule-dialog {
    width: min(720px, calc(100vw - 24px)) !important;
  }

  .create-schedule-dialog .el-dialog__header,
  .create-schedule-dialog .el-dialog__body,
  .create-schedule-dialog .el-dialog__footer {
    padding-left: 20px;
    padding-right: 20px;
  }
}
@media (min-width: 601px) {
  .create-schedule-dialog .el-form-item__label {
    flex: 0 0 170px !important;
    width: 170px !important;
    min-width: 170px !important;
  }

  .create-schedule-dialog .el-form-item__content {
    flex: 1 1 auto !important;
    margin-left: 0 !important;
  }
}
@media (max-width: 480px) {
  .create-schedule-dialog {
    width: calc(100vw - 20px) !important;
    max-width: calc(100vw - 20px) !important;
    margin: 0 !important;
  }
  .create-schedule-dialog .el-dialog__header {
    padding: 14px 14px 6px !important;
  }
  .create-schedule-dialog .el-dialog__title {
    font-size: 18px !important;
  }
  .create-schedule-dialog .el-dialog__body {
    padding: 16px 14px 8px !important;
    max-height: none !important;
    flex: 1 1 auto !important;
    overflow-y: auto !important;
  }
  .create-schedule-dialog .el-dialog__footer {
    padding: 6px 14px 12px !important;
    gap: 8px !important;
  }
  .create-schedule-dialog .dialog-footer-btn {
    min-width: 100px !important;
    height: 38px !important;
    min-height: 38px !important;
    padding: 0 16px !important;
    font-size: 13px !important;
  }
  .create-schedule-dialog .el-form-item__label {
    font-size: 12px !important;
    min-height: 32px !important;
    text-align: left !important;
    justify-content: flex-start !important;
    padding-right: 6px !important;
  }
  .create-schedule-dialog .el-input__wrapper,
  .create-schedule-dialog .el-select__wrapper {
    min-height: 34px !important;
  }
  .create-schedule-dialog .template-apply-btn {
    min-height: 28px !important;
    font-size: 11px !important;
    padding: 0 12px !important;
  }
}

/* Compact calendar cells inside create dialog */
.create-schedule-dialog .el-calendar-day,
.create-schedule-dialog .shift-calendar--edit ::v-deep(.el-calendar-day) {
  height: 52px !important;
  min-height: 52px !important;
}
.create-schedule-dialog .cell-date,
.create-schedule-dialog .shift-calendar--edit .cell-date {
  font-size: 32px !important;
  color: #ffffff !important;
  font-weight: 300 !important;
}
.create-schedule-dialog .el-calendar-table td {
  opacity: 1 !important;
  padding: 0 !important;
  height: auto !important;
  vertical-align: top !important;
}

.create-schedule-dialog .el-calendar__body,
.create-schedule-dialog .shift-calendar--edit ::v-deep(.el-calendar__body),
.create-schedule-dialog .el-calendar-table,
.create-schedule-dialog .shift-calendar--edit ::v-deep(.el-calendar-table),
.create-schedule-dialog .el-calendar-table tbody,
.create-schedule-dialog .shift-calendar--edit ::v-deep(.el-calendar-table tbody),
.create-schedule-dialog .el-calendar-table tr,
.create-schedule-dialog .shift-calendar--edit ::v-deep(.el-calendar-table tr) {
  height: auto !important;
}
.create-schedule-dialog .el-calendar-table td:not(.prev):not(.next) .cell-date {
  color: #ffffff !important;
  font-weight: 300 !important;
}
.create-schedule-dialog .el-calendar-table td.prev .cell-date,
.create-schedule-dialog .el-calendar-table td.next .cell-date {
  color: #000000 !important;
  font-weight: 300 !important;
}
.create-schedule-dialog .el-calendar-table td.is-today .cell-date {
  color: #ffffff !important;
}
.create-schedule-dialog .calendar-date-click-area.selected .cell-date {
  width: 44px !important;
  height: 44px !important;
  line-height: 44px !important;
  font-size: 24px !important;
}
.create-schedule-dialog .cell-shift-inline {
  font-size: 11px !important;
  margin-top: 2px !important;
}
.create-schedule-dialog .shift-calendar-title,
.create-schedule-dialog .shift-calendar--edit .shift-calendar-title {
  font-size: 26px !important;
  color: #ffffff !important;
}
.create-schedule-dialog .el-calendar-table th,
.create-schedule-dialog .shift-calendar--edit ::v-deep(.el-calendar-table th) {
  font-size: 15px !important;
  padding-bottom: 1px !important;
  font-weight: 300 !important;
}
.create-schedule-dialog .shift-calendar-nav,
.create-schedule-dialog .shift-calendar--edit .shift-calendar-nav {
  gap: 10px !important;
  align-items: center !important;
  padding-bottom: 0 !important;
  flex-wrap: nowrap !important;
  justify-content: flex-end !important;
}

.create-schedule-dialog .shift-calendar-nav-btn,
.create-schedule-dialog .shift-calendar--edit .shift-calendar-nav-btn {
  font-size: 12px !important;
  font-weight: 300 !important;
}

.create-schedule-dialog.el-dialog {
  width: min(760px, calc(100vw - 40px)) !important;
  max-height: calc(100vh - 40px) !important;
  overflow: hidden !important;
  border-radius: 0 !important;
  margin: 20px auto !important;
  background-color: #10244d !important;
  background-image:
    linear-gradient(rgb(0 108 254 / 22%), rgb(0 108 254 / 22%)),
    linear-gradient(90deg, #030b1b 0%, #15264e 100%) !important;
  display: flex !important;
  flex-direction: column !important;
}

.create-schedule-dialog .calendar-date-click-area.disabled {
  opacity: 1 !important;
}

.create-schedule-dialog .el-calendar-table td:not(.prev):not(.next) .calendar-date-click-area.disabled .cell-date,
.create-schedule-dialog .el-calendar-table td.is-today .calendar-date-click-area.disabled .cell-date {
  color: #ffffff !important;
}

.create-schedule-dialog .el-calendar-table td.prev .calendar-date-click-area.disabled .cell-date,
.create-schedule-dialog .el-calendar-table td.next .calendar-date-click-area.disabled .cell-date {
  color: #000000 !important;
}

@media (max-width: 600px) {
  .create-schedule-dialog .el-calendar-day,
  .create-schedule-dialog .shift-calendar--edit ::v-deep(.el-calendar-day) {
    height: 28px !important;
    min-height: 28px !important;
  }
  .create-schedule-dialog .cell-date,
  .create-schedule-dialog .shift-calendar--edit .cell-date {
    font-size: 18px !important;
  }
  .create-schedule-dialog .calendar-date-inner,
  .create-schedule-dialog .shift-calendar--edit .calendar-date-inner {
    padding-top: 0 !important;
  }
  .create-schedule-dialog .calendar-date-click-area.selected .calendar-date-inner,
  .create-schedule-dialog .shift-calendar--edit .calendar-date-click-area.selected .calendar-date-inner {
    padding-top: 0 !important;
  }
  .create-schedule-dialog .shift-calendar-title,
  .create-schedule-dialog .shift-calendar--edit .shift-calendar-title {
    font-size: 22px !important;
  }
  .create-schedule-dialog .shift-calendar-header,
  .create-schedule-dialog .shift-calendar--edit .shift-calendar-header {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 6px !important;
  }
  .create-schedule-dialog .shift-calendar-nav,
  .create-schedule-dialog .shift-calendar--edit .shift-calendar-nav {
    gap: 10px !important;
    flex-wrap: nowrap !important;
    justify-content: flex-end !important;
    width: 100% !important;
    margin-left: 0 !important;
    flex: 0 0 auto !important;
    overflow-x: auto !important;
    overflow-y: hidden !important;
    scrollbar-width: none;
    -ms-overflow-style: none;
    padding-bottom: 2px !important;
  }
  .create-schedule-dialog .shift-calendar-nav::-webkit-scrollbar,
  .create-schedule-dialog .shift-calendar--edit .shift-calendar-nav::-webkit-scrollbar {
    display: none;
  }
  .create-schedule-dialog .shift-calendar-nav-btn,
  .create-schedule-dialog .shift-calendar--edit .shift-calendar-nav-btn {
    flex: 0 0 auto !important;
    font-size: 10px !important;
  }
  .create-schedule-dialog .el-calendar-table th {
    font-size: 12px !important;
    padding-bottom: 0 !important;
  }
  .create-schedule-dialog .calendar-date-click-area.selected .cell-date {
    width: 27px !important;
    height: 27px !important;
    line-height: 27px !important;
    font-size: 15px !important;
  }
  .create-schedule-dialog .cell-shift-inline {
    display: none !important;
  }
}

@media (max-width: 390px) {
  .create-schedule-dialog .shift-calendar-title,
  .create-schedule-dialog .shift-calendar--edit .shift-calendar-title {
    font-size: 13px !important;
  }

  .create-schedule-dialog .shift-calendar-nav-btn,
  .create-schedule-dialog .shift-calendar--edit .shift-calendar-nav-btn {
    font-size: 7px !important;
  }

  .create-schedule-dialog .el-calendar-day,
  .create-schedule-dialog .shift-calendar--edit ::v-deep(.el-calendar-day) {
    height: 24px !important;
    min-height: 24px !important;
  }

  .create-schedule-dialog .cell-date,
  .create-schedule-dialog .shift-calendar--edit .cell-date {
    font-size: 13px !important;
  }
}
</style>
<style scoped>
.el-form-item {
  margin-bottom: 16px;
}
.employee-error-alert {
  margin-top: 8px;
}
.template-actions {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.template-actions .el-button {
  min-height: 34px;
  padding-inline: 16px;
  font-size: 11px;
}
.spacer {
  flex: 1;
}
.calendar-placeholder {
  border: 1px solid var(--ui-color-border-light);
  border-radius: 4px;
  padding: 8px;
}
@media (max-width: 480px) {
  .el-form-item {
    margin-bottom: 8px;
  }
}
</style>
